<script setup lang="ts">
// Email → 6-digit code verification gate. Transparent copy explains exactly
// why the email is needed (AI cost, 100-interaction cap) — no dark patterns.
// Emits `verified` once /api/identity/me confirms the cookie landed.
import { computed, ref, watch } from 'vue'
import { NButton, NInput } from 'naive-ui'
import { useIdentity } from '@/shared/composables/useIdentity'

const emit = defineEmits<{ verified: [] }>()

const { me, start, verify } = useIdentity()

const email = ref('')
const code = ref('')
const step = ref<'email' | 'code'>('email')

const alreadyVerified = computed(() => me.data.value?.verified === true)
watch(
    alreadyVerified,
    (v) => {
        if (v) emit('verified')
    },
    { immediate: true },
)

const emailValid = computed(() => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.value.trim()))
const codeValid = computed(() => /^\d{6}$/.test(code.value.trim()))

async function sendCode() {
    if (!emailValid.value) return
    await start.mutateAsync(email.value.trim())
    step.value = 'code'
}

async function submitCode() {
    if (!codeValid.value) return
    await verify.mutateAsync({ email: email.value.trim(), code: code.value.trim() })
    emit('verified')
}
</script>

<template>
    <div v-if="!alreadyVerified" class="verify-gate">
        <h3>One quick step: verify your email</h3>
        <p class="muted why">
            Running the AI analysis costs us real money, so each verified email gets
            <strong>100 free AI interactions</strong>. This keeps the tool free for everyone —
            no account, no password, no spam.
        </p>

        <template v-if="step === 'email'">
            <div class="row">
                <NInput
                    v-model:value="email"
                    type="text"
                    inputmode="email"
                    placeholder="you@example.com"
                    size="large"
                    :disabled="start.isPending.value"
                    @keyup.enter="sendCode"
                />
                <NButton
                    type="primary"
                    size="large"
                    :loading="start.isPending.value"
                    :disabled="!emailValid"
                    @click="sendCode"
                >
                    Send Code
                </NButton>
            </div>
            <p v-if="start.isError.value" class="error">
                {{ (start.error.value as Error)?.message ?? 'Something went wrong — try again.' }}
            </p>
        </template>

        <template v-else>
            <p class="muted">We emailed a 6-digit code to <strong>{{ email }}</strong>.</p>
            <div class="row">
                <NInput
                    v-model:value="code"
                    placeholder="123456"
                    size="large"
                    :maxlength="6"
                    :disabled="verify.isPending.value"
                    @keyup.enter="submitCode"
                />
                <NButton
                    type="primary"
                    size="large"
                    :loading="verify.isPending.value"
                    :disabled="!codeValid"
                    @click="submitCode"
                >
                    Verify
                </NButton>
            </div>
            <p v-if="verify.isError.value" class="error">
                {{ (verify.error.value as Error)?.message ?? 'Invalid code — try again.' }}
            </p>
            <button class="linkish" type="button" @click="step = 'email'">
                Use a different email
            </button>
        </template>
    </div>
</template>

<style scoped lang="scss">
.verify-gate {
    h3 {
        margin-bottom: var(--site-space-sm);
    }

    .why {
        max-width: 34rem;
    }

    .row {
        display: flex;
        gap: var(--site-space-sm);
        max-width: 26rem;
    }

    .error {
        color: var(--site-danger);
        margin-top: var(--site-space-sm);
        font-size: 0.9rem;
    }

    .linkish {
        background: none;
        border: none;
        padding: 0;
        margin-top: var(--site-space-sm);
        color: var(--site-accent);
        cursor: pointer;
        font-size: 0.9rem;
    }
}
</style>
